function hexToLinear(channel: number): number {
	const c = channel / 255;
	return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

type OklchColor = { l: number; c: number; h: number };

/**
 * Converts a hex color string (e.g. "#1a2b3c") to OKLCH components.
 * Returns null for invalid input.
 */
export function hexToOklch(hex: string): OklchColor | null {
	const clean = hex.replace("#", "");
	const full =
		clean.length === 3
			? clean
					.split("")
					.map((ch) => ch + ch)
					.join("")
			: clean;

	if (full.length !== 6) return null;

	const r = hexToLinear(Number.parseInt(full.slice(0, 2), 16));
	const g = hexToLinear(Number.parseInt(full.slice(2, 4), 16));
	const b = hexToLinear(Number.parseInt(full.slice(4, 6), 16));

	// sRGB linear → XYZ (D65)
	const x = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
	const y = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
	const z = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

	// XYZ → OKLab
	const l = Math.cbrt(0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z);
	const m = Math.cbrt(0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z);
	const s = Math.cbrt(0.0482003018 * x + 0.2643662691 * y + 0.633851707 * z);

	const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
	const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
	const bLab = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

	// OKLab → OKLCH
	const C = Math.sqrt(a * a + bLab * bLab);
	const H = (Math.atan2(bLab, a) * 180) / Math.PI;
	const hue = H < 0 ? H + 360 : H;

	return { l: L, c: C, h: hue };
}

/**
 * Formats OKLCH components as a CSS oklch() string.
 */
export function formatOklch(l: number, c: number, h: number): string {
	return `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${h.toFixed(1)})`;
}

/**
 * Converts a hex color to a CSS oklch() string.
 * Returns undefined for invalid input (used by legacy callers).
 */
export function hexToOklchString(hex: string): string | undefined {
	const color = hexToOklch(hex);
	if (!color) return undefined;
	return formatOklch(color.l, color.c, color.h);
}
